/**
 * Competition channels modal (phase 4): counts of the server's plan, whether a server can be ticked (never pre-ticked:
 * the user ticks each one), and the scope of "retry the failed ones". Pure: the modal calls plan / run itself.
 */
import type { CompPlanServer, CompServerResult } from '../services/channels.service';

export function planCounts(s: CompPlanServer): { act: number; alreadyDone: number; existsOnServer: number; conflict: number; unknown: number; removed: number } {
  const c = { act: 0, alreadyDone: 0, existsOnServer: 0, conflict: 0, unknown: 0, removed: 0 };
  for (const i of s.items) {
    if (i.act) c.act++;
    else if (i.alreadyDone) c.alreadyDone++;
    else if (i.skipped === 'exists-on-server') c.existsOnServer++;
    else if (i.skipped === 'conflict') c.conflict++;
    else if (i.skipped === 'unknown-state' || i.skipped === 'unreachable') c.unknown++;
    else if (i.skipped === 'server-removed') c.removed++;
  }
  return c;
}

/** A server can be ticked only when it is still in the account, answered, and the action would do something there. */
export function serverChoice(s: CompPlanServer): { selectable: true } | { selectable: false; reasonKey: string } {
  if (s.removed) return { selectable: false, reasonKey: 'compChannels.reason.removed' };
  if (!s.reachable) return { selectable: false, reasonKey: 'compChannels.reason.unreachable' };
  if (!s.items.some((i) => i.act)) return { selectable: false, reasonKey: 'compChannels.reason.nothingToDo' };
  return { selectable: true };
}

/** Servers where something failed or could not be reached, and the rows whose delete failed. */
export function retryScope(results: CompServerResult[]): { servers: number[]; legIds: number[] } {
  const servers = results.filter((r) => r.failed.length > 0 || !r.reachable).map((r) => r.serverId);
  const legIds = results.flatMap((r) => r.failed.map((f) => f.legId).filter((x): x is number => typeof x === 'number'));
  return { servers, legIds };
}

/** Wizard retry: per channel, the servers where its legs failed or could not be reached (never a server that worked). */
export function bulkRetryChannels(results: Array<{ serverId: number; failed: Array<{ channelId: number }>; skipped: Array<{ channelId: number; reason: string }> }>): Array<{ id: number; servers: number[] }> {
  const by = new Map<number, Set<number>>();
  for (const r of results) {
    for (const f of r.failed) { if (!by.has(f.channelId)) by.set(f.channelId, new Set()); by.get(f.channelId)!.add(r.serverId); }
    for (const sk of r.skipped) if (sk.reason === 'unreachable' || sk.reason === 'unknown-state') { if (!by.has(sk.channelId)) by.set(sk.channelId, new Set()); by.get(sk.channelId)!.add(r.serverId); }
  }
  return [...by.entries()].map(([id, s]) => ({ id, servers: [...s].sort((a, b) => a - b) }));
}

/** Competition API error codes with a fixed meaning: shown translated; anything else with the server's text. */
const KNOWN_COMP_API_ERRORS = new Set(['SYNC_BUSY']);
export function compApiErrorText(err: { code?: string; message?: string }, t: (key: string) => string): string {
  if (err.code && KNOWN_COMP_API_ERRORS.has(err.code)) return t(`competitions.apiErrors.${err.code}`);
  return err.message || '';
}
